import type { UploadFile } from "antd/es/upload/interface";
import { notification, Form } from "antd";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import axiosInstance from "@lhd/libs/axios";
import { chatCatalogRequest } from "../../../../hooks/chatCatalog";
import { SERVICES } from "@lhd/constants";
import { handleMessageError } from "@lhd/features/Apps/utils";
import { getCompanyId } from "@lhd/utility";
import { ALLOWED_FILE_TYPES, ALLOWED_MIME_TYPES } from "../ModalChat/hook";
import { useEffect, useRef, useState } from "react";
import { IOption } from "../ChatRealTime/hook";
import {
  getOrCreateMessageSubmission,
  MessageSubmissionCache,
  MessageSubmissionFile,
} from "../ChatRealTime/messageSubmission";
import { createThreadRequest } from "./request";
export { createThreadRequest } from "./request";

export type ReceivedProps = {
  handleBackToList?: () => void;
  handleSuccess: (dataMessage: any) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  isFullscreen?: boolean;
};
interface FormValues {
  title?: string;
  category_id?: string;
  content?: string;
  files?: UploadFile<File>[];
}

const useModalCreateChat = (props: ReceivedProps) => {
  const [categoryOptions, setCategoryOptions] = useState<IOption[]>([]);
  const [isLoading, setListLoading] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm<FormValues>();
  const companyId = getCompanyId();
  const pendingNewInquiryRef = useRef<{
    thread: any;
    submission: MessageSubmissionCache;
  } | null>(null);
  const submittingRef = useRef(false);

  const handleGetCategories = async () => {
    if (!companyId) return;
    try {
      setListLoading(true);
      const { data } = await axiosInstance(
        chatCatalogRequest("category", companyId),
      );
      const options = data?.map((item: any) => ({
        label: item?.name,
        value: item?.id,
      }));
      setCategoryOptions(options);
      setListLoading(false);
    } catch (error: any) {
      setListLoading(false);
      notification["error"]({
        message: handleMessageError(error?.response?.data?.error),
      });
    }
  };

  const handleUploadFiles = async (files: File[]) => {
    if (files?.length === 0) return;

    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files[][file]", file);
    });

    const res = await axiosInstanceV2.post(
      `${SERVICES.CHAT_ATTACHMENTS}/upload_multiple`,
      formData,
    );

    return res.data;
  };

  const handleSubmit = async (values: FormValues) => {
    if (!companyId || submittingRef.current) return;
    submittingRef.current = true;
    try {
      setListLoading(true);
      const content = values.content?.trim();
      const files = fileList as MessageSubmissionFile[];
      const previousPending = pendingNewInquiryRef.current;
      const submission = getOrCreateMessageSubmission(
        previousPending?.submission,
        content || "",
        files,
      );
      const canReuseThread =
        previousPending?.submission.signature === submission.signature;

      const valuesCreateThread = createThreadRequest(values, companyId);

      let data = canReuseThread ? previousPending?.thread : null;
      if (!data) {
        const response: { data: any } = await axiosInstanceV2({
          method: "post",
          url: SERVICES.CHAT_THREADS,
          data: valuesCreateThread,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        data = response.data;
        if (!data?.id) throw new Error("問い合わせの作成に失敗しました");
      }

      // Keep the thread and idempotency key until the first message is
      // acknowledged. A lost response can therefore retry the same message
      // instead of creating a second thread and AI run.
      pendingNewInquiryRef.current = { thread: data, submission };

      if (data?.id) {
        const formData = new FormData();
        formData.append("content", content || "");
        formData.append("client_request_id", submission.clientRequestId);
        let resFiles = submission.uploadedAttachments;
        if (resFiles === null) {
          const filesToUpload: any = fileList?.map(
            (file) => file.originFileObj,
          );
          resFiles = (await handleUploadFiles(filesToUpload)) || [];
          submission.uploadedAttachments = resFiles;
        }
        const uploadedFiles = resFiles || [];
        if (uploadedFiles.length > 0) {
          uploadedFiles.forEach((file: any) => {
            formData.append("attachments[][file_url]", file?.file_url);
            formData.append("attachments[][content_type]", file?.content_type);
            formData.append("attachments[][file_size]", file?.file_size);
            formData.append("attachments[][filename]", file?.filename);
            formData.append(
              "attachments[][storage_provider]",
              file?.storage_provider,
            );
          });
        }
        const res = await axiosInstanceV2({
          method: "post",
          url: `${SERVICES.THREADS}/${data?.id}/messages`,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        pendingNewInquiryRef.current = null;
        props.handleSuccess({ ...res?.data, thread: data });
      }
    } catch (error: any) {
      notification["error"]({
        message: handleMessageError(error?.response?.data?.error),
      });
    } finally {
      submittingRef.current = false;
      setListLoading(false);
    }
  };

  const handleValuesChange = (_: any, allValues: FormValues) => {
    const isDirty =
      !!allValues.title ||
      !!allValues.category_id ||
      !!allValues.content ||
      !!(allValues.files && allValues.files.length > 0);

    props.onDirtyChange?.(isDirty);

    const isValid = !!allValues.category_id && !!allValues.content?.trim();
    setCanSubmit(isValid);
  };

  useEffect(() => {
    handleGetCategories();
  }, []);
  return {
    ...props,
    handleSubmit,
    form,
    ALLOWED_FILE_TYPES,
    ALLOWED_MIME_TYPES,
    categoryOptions,
    isLoading,
    fileList,
    setFileList,
    handleValuesChange,
    canSubmit,
  };
};

export type Props = ReturnType<typeof useModalCreateChat>;

export default useModalCreateChat;
